/* eslint-disable react-dom/no-unsafe-iframe-sandbox */
import { Button, RadioGroup, RadioGroupItem, Text } from "@sentricmusic/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaInfoCircle } from "react-icons/fa";
import short from "short-uuid";
import { toast } from "sonner";

import {
  useCreateRecording,
  useUpdateRecordingMeta,
  useUpdateSuggestion,
} from "@/api/mutations";
import {
  useGetArtistMeta,
  useGetMyAccount,
  useGetMyProfile,
  useGetMySong,
  useGetMySongs,
  useGetSuggestions,
} from "@/api/queries";
import { hasAdminRights } from "@/auth/shared";
import FormError from "@/components/FormError";
import RebrandButtonLink from "@/components/RebrandButtonLink";
import RebrandPrimaryButton from "@/components/RebrandPrimaryButton";
import Select from "@/components/Select";
import Spacer from "@/components/Spacer";
import { sanitizeInput, sendEvent, sendProperty } from "@/helpers/helpers";
import useStringSimilarity from "@/hooks/useStringSimilarity";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import SongsCreateUpdate from "./SongsCreateUpdate.tsx";

function SongSuggestionDetails(props) {
  const { t } = useTranslation("songs");
  const flags = useFlags();
  const { row } = props;
  const { original } = row;
  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const songs = useGetMySongs({ aac: selectedAccount });
  const [songList, setSongList] = useState([]);
  const [originalWriter, setOriginalWriter] = useState(null);
  const [newSong, setNewSong] = useState(null);
  const [pending, setPending] = useState(false);
  const artistMeta = useGetArtistMeta({ aac: selectedAccount });
  const spotifyID = (() => {
    try {
      return artistMeta?.data?.spotify_obj
        ? JSON.parse(artistMeta.data.spotify_obj)?.id
        : null;
    }
    catch (error) {
      console.warn("[SongSuggestionDetails] Invalid JSON in spotify_obj:", artistMeta?.data?.spotify_obj, error);
      return null;
    }
  })();
  const suggestions = useGetSuggestions({
    spotifyid: spotifyID,
    accountId: selectedAccount,
  });
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  const isTunecore = useUIStateStore(state => state.isTunecore);
  const profile = useGetMyProfile();
  const { compareTwoStrings } = useStringSimilarity();

  const updateSuggestionMutation = useUpdateSuggestion();
  const recordingCreateMutation = useCreateRecording();
  const recordingMetaMutation = useUpdateRecordingMeta();
  const [selectedSong, setSelectedSong] = useState(null);
  const [suggestedSong, setSuggestedSong] = useState(null);
  const [showManualSearch, setShowManualSearch] = useState(false);
  const radioGroupsRef = useRef(null);

  const cancel = () => {
    setOriginalWriter(null);
    setNewSong(null);
    setSuggestedSong(null);
    setSelectedSong(null);
    setShowManualSearch(false);
  };

  const songTemp = useGetMySong({
    aac: selectedAccount,
    wc: selectedSong?.workcode,
  });

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const adminRights = hasAdminRights(myAccount?.data);

  // Initialize song list when songs data is available
  useEffect(() => {
    if (songs.data?.works) {
      setSongList([...songs.data.works]);
    }
  }, [songs.data?.works]);

  const findSuggestedMatches = useCallback(() => {
    if (!songs.data?.works)
      return;

    const ms = songs.data.works.filter(
      s => compareTwoStrings(s.title, original.title) > 0.7,
    );

    if (ms.length > 0) {
      setSuggestedSong(ms[0]);
      setSelectedSong(ms[0]);
      setShowManualSearch(false);
      setSongList([
        ...ms,
        ...songs.data.works.filter(w => !ms.includes(w)),
      ]);
    }
    else {
      setSuggestedSong(null);
      setSelectedSong(null);
      setShowManualSearch(true);
      setSongList([...songs.data.works]);
    }
  }, [songs.data?.works, compareTwoStrings, original.title]);

  // Run suggestion matching when user selects "song already exists" (only if not manually searching)
  useEffect(() => {
    if (newSong === "false" && songs.data?.works && !showManualSearch) {
      findSuggestedMatches();
    }
  }, [newSong, songs.data?.works, findSuggestedMatches, showManualSearch]);

  const updateSuggestionStatus = ({ status, id }) => {
    /*
    0 = shown in suggestions list
    1 = added as a song
    2 = added as recording
    3 = ignore
    */
    setPending(true);

    updateSuggestionMutation.reset();
    const data = {
      status,
      id,
    };
    updateSuggestionMutation.mutate(data, {
      onSuccess: () => {
        suggestions.refetch();
        setPending(false);
      },
      onError: (error) => {
        console.log(`update sugestion ${error}`);
        setPending(false);
      },
    });
  };

  const addRecording = async ({ suggestion }) => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    // reset mutation
    recordingCreateMutation.reset();

    const data = {
      artistaccountcode: selectedAccount,
      title: sanitizeInput(suggestion.title),
      performingartist: sanitizeInput(suggestion.artists.split(",")[0]),
      isrc: suggestion.isrc,
      releasedate: suggestion.releasedate,
      workcode: songTemp.data.workcode,
      uicbarcode: short.generate().substring(0, 13),
      spotifyidentifier: suggestion?.spotify_song_id,
      spotifypopularity: suggestion?.popularity,
      spotifypopularitylastupdatedutc: suggestion?.spotify_song_id === undefined
        || suggestion?.spotify_song_id === null
        ? null
        : new Date().toISOString(),
      etag: songTemp?.data?.etag,
    };

    recordingCreateMutation.mutate(data, {
      onSuccess: (response) => {
        if (response.failedvalidation) {
          setValidationErrorMessage(response.messages.join(", "));
          setPending(false);
        }
        else {
          // eslint-disable-next-line no-use-before-define
          addRecordingMeta(response.model.uicbarcode, suggestion);
        }
      },
      onError: (error) => {
        console.log(`error on addRecording ${error.message}`);
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  const addRecordingMeta = (rid, suggestion) => {
    setMutationErrorMessage("");
    setValidationErrorMessage("");

    recordingMetaMutation.reset();
    const data = {
      id: rid,
      spotify_id: suggestion.spotify_song_id,
      mp3: null,
      thumb_url: suggestion?.images
        ? (() => {
            try {
              return JSON.parse(suggestion.images)?.[0]?.url || null;
            }
            catch (error) {
              console.warn("[SongSuggestionDetails] Invalid JSON in suggestion.images:", suggestion.images, error);
              return null;
            }
          })()
        : null,
      song_id: songTemp.data.workcode,
    };

    recordingMetaMutation.mutate(data, {
      onSuccess: (response) => {
        console.log("Recording Meta Response:", response);
        setValidationErrorMessage("");
        setMutationErrorMessage("");

        if (response.failedvalidation) {
          const errorMessages = response?.data?.messages?.join(", ") || "Validation failed, but no specific messages were provided.";
          console.log(`failed validation: ${errorMessages}`);
          setValidationErrorMessage(errorMessages);
          setPending(false);
        }
        else {
          console.log(`validation success (Meta update successful)`);
          toast.success(`${t("songs:suggestion-details-toast-success")}`);
          sendProperty({
            property: "last_recording_added",
            value: "CURRENT_DATE",
            email: profile?.data?.email,
          });
          sendEvent({ property: "pe14489857_recording_added" });
          updateSuggestionStatus({ status: 2, id: suggestion.id });
          songs.refetch();
          setPending(false);
        }
      },
      onError: (error) => {
        console.error(
          `Error during recording meta update mutation: ${error.message}`,
          error,
        );
        setValidationErrorMessage("");
        setMutationErrorMessage(
          error.message || "An unknown error occurred during the meta update.",
        );
        setPending(false);
      },
    });
  };

  if (!flags["pd-spotify-suggestions-feature"]) {
    return <tr><td colSpan={6} /></tr>;
  }

  return (
    <tr>
      <td colSpan="6">
        <div
          ref={radioGroupsRef}
          className={`rounded-lg ${
            originalWriter === null
            || (originalWriter === "true" && newSong === null)
              ? "mb-2"
              : "-mb-2 z-10 relative"
          } ${
            isTunecore ? "bg-tunecore-black" : "bg-sentric-rebrand-light-grey"
          } lg:p-6 p-4 mt-2 text-old-song-sub-color lg:w-screen-width-minus-gutter w-full`}
        >
          <div className="flex flex-wrap">
            <div className="flex items-center w-full lg:w-1/2">
              <p
                className={
                  isTunecore
                    ? "text-white text-base"
                    : "text-black font-medium text-base"
                }
              >
                {original.title}
              </p>
            </div>
          </div>
          {original.isrc && (
            <p
              className={`${
                isTunecore ? "text-white" : "text-sentric-rebrand-grey-text"
              } text-xxs `}
            >
              <span className="font-semi-bold">ISRC: </span>
              {original.isrc}
            </p>
          )}
          <Spacer thin />
          <div
            className={`flex flex-wrap ${
              isTunecore ? "text-white" : "text-sentric-rebrand-grey-text"
            }`}
          >
            <div className="w-full lg:w-1/2">
              <p className="text-xs">
                <span className="font-semibold uppercase">
                  {t("songs:performed-by")}
                  :
                </span>
                {" "}
                {original.artists.replace(/&#44;/g, ",")}
              </p>
            </div>
            <div className="w-full lg:w-1/2">
              <p className="text-xs">
                <span className="font-semibold uppercase">
                  {t("songs:duration")}
                  :
                </span>
                {" "}
                {Math.floor(original.duration / 1000 / 60)}
                {" "}
                {t("common:minutes")}
                {" "}
                {Math.floor((original.duration / 1000) % 60)}
                {" "}
                {t("common:seconds")}
              </p>
            </div>
          </div>
          <Spacer thin />
          {original.spotify_song_id && (
            <iframe
              title="preview"
              src={`https://open.spotify.com/embed/track/${original.spotify_song_id}`}
              className="w-full lg:w-5/12"
              height="80"
              allow="encrypted-media"
              sandbox="allow-scripts allow-same-origin"
            />
          )}
          <Spacer />
          <Spacer thin />
          <div>
            <h2 className={`${isTunecore ? "text-white" : "text-black"} font-medium text-base mb-2`}>
              {t("songs:original-writer-header")}
            </h2>
            <RadioGroup
              value={originalWriter?.toString() || ""}
              onValueChange={(value) => {
                if (adminRights) {
                  setOriginalWriter(value === "true" ? "true" : "false");
                }
              }}
              disabled={!adminRights}
              className="flex flex-col gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="true" id="originalwriter-true" />
                <label htmlFor="originalwriter-true" className={`text-sm font-medium cursor-pointer ${isTunecore ? "text-white" : "text-black"} ${!adminRights ? "opacity-40" : ""}`}>
                  {t("songs:original-writer-confirm")}
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="false" id="originalwriter-false" />
                <label htmlFor="originalwriter-false" className={`text-sm font-medium cursor-pointer ${isTunecore ? "text-white" : "text-black"} ${!adminRights ? "opacity-40" : ""}`}>
                  {t("songs:original-writer-deny")}
                </label>
              </div>
            </RadioGroup>
          </div>
          {originalWriter === "true" && (
            <>
              <Spacer />
              <Spacer thin />
              <h2
                className={`${
                  isTunecore ? "text-white" : "text-black"
                } mb-2 font-medium text-base`}
              >
                {t("songs:is-this-a-new-song")}
              </h2>
              {selectedSong && (
                <p
                  className={
                    isTunecore
                      ? "text-white"
                      : "text-sentric-rebrand-grey-text font-sentric-alt"
                  }
                >
                  <FaInfoCircle className="inline mr-2" />
                  {t("songs:your-existing-song")}
                  {" "}
                  <b>
                    "
                    {selectedSong?.title}
                    "
                  </b>
                  {" "}
                  {t("songs:looks-like-a-match")}
                </p>
              )}
              <Spacer thin />

              <RadioGroup
                value={newSong?.toString() || ""}
                onValueChange={value => setNewSong(value === "true" ? "true" : "false")}
                className="flex flex-col gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="newsong-true" />
                  <label htmlFor="newsong-true" className={`text-sm font-medium cursor-pointer ${isTunecore ? "text-white" : "text-black"}`}>
                    {t("songs:this-is-a-new-song-confirm")}
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="newsong-false" />
                  <label htmlFor="newsong-false" className={`text-sm font-medium cursor-pointer ${isTunecore ? "text-white" : "text-black"}`}>
                    {t("songs:song-already-exists")}
                  </label>
                </div>
              </RadioGroup>
            </>
          )}
        </div>

        {originalWriter === "false" && (
          <div
            className={`${
              isTunecore ? "bg-tunecore-black" : "bg-sentric-rebrand-light-grey"
            }  lg:p-6 p-4 rounded-t-none rounded-lg mb-2 flex flex-wrap justify-end items-center`}
          >
            <div className="mx-2">
              <button
                onClick={() => {
                  cancel();
                }}
                type="button"
                className={`${
                  isTunecore
                    ? "text-white hover:text-tunecore-green"
                    : "text-black"
                } flex items-center underline`}
              >
                {t("songs:cancel-and-discard")}
              </button>
            </div>
            <div className="mx-2">
              <RebrandPrimaryButton
                onClick={() => {
                  updateSuggestionStatus({ status: 3, id: original.id });
                  sendEvent({
                    property: "pe14489857_spotify_suggestion_removed",
                  });
                }}
                disabled={pending}
                pending={pending}
              >
                {t("songs:remove-suggestion")}
              </RebrandPrimaryButton>
            </div>
          </div>
        )}

        {newSong === "false" && (
          <div
            className={`${
              isTunecore ? "bg-tunecore-black" : "bg-sentric-rebrand-light-grey"
            } lg:p-6 p-4 rounded-t-none rounded-lg mb-2`}
          >
            <div className="flex items-center justify-between mb-4">
              <h2
                className={`${
                  isTunecore ? "text-white" : "text-black"
                } text-xl`}
              >
                {t("songs:search-for-existing-and-select")}
              </h2>
              <Button
                onClick={() => {
                  setShowManualSearch(false);
                  findSuggestedMatches();
                }}
                type="button"
                variant="link"
                label="Refresh matches"
                tooltip
                tooltipText="Find suggested matches"
              />
            </div>

            {suggestedSong && !showManualSearch && (
              <div className={`p-4 mb-4 rounded-lg border ${
                isTunecore
                  ? "bg-tunecore-mid-grey border-tunecore-light-grey"
                  : "bg-white border-sentric-light-grey"
              }`}
              >
                <Text as="p" className={`${isTunecore ? "text-white" : "text-green-800"} mb-2`}>
                  <span className="font-semibold">Suggested match found: </span>
                  "
                  {suggestedSong.title}
                  "
                </Text>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setSuggestedSong(null);
                      setSelectedSong(null);
                      setShowManualSearch(true);
                    }}
                    type="button"
                    variant="link"
                    label="Choose different song"
                  />
                </div>
              </div>
            )}

            {showManualSearch && (
              <>
                {!suggestedSong && (
                  <div className={`p-4 mb-4 rounded-lg border ${
                    isTunecore
                      ? "bg-tunecore-mid-grey border-tunecore-light-grey"
                      : "bg-white border-sentric-light-grey"
                  }`}
                  >
                    <Text as="p" className={`text-sm ${isTunecore ? "text-white" : "text-black"}`}>
                      No automatic matches found. Please search manually below.
                    </Text>
                  </div>
                )}
                <div className="relative">
                  <Select
                    options={songList.map(m => ({
                      name: m.title,
                      value: m.workcode,
                    }))}
                    onChange={(item) => {
                      setSelectedSong(songList.find(s => s.workcode === item));
                    }}
                    value={selectedSong?.workcode}
                    className="w-full text-base select-search select-search--multiple font-sentric-alt text-sentric-rebrand-grey-text required-star lg:w-1/2"
                    search
                    filterOptions
                    placeholder={t("songs:search-for-existing-placeholder")}
                  />
                </div>
              </>
            )}
            {selectedSong && songTemp.isSuccess && (
              <>
                <Spacer thin />
                <div className={`p-4 mb-4 rounded-lg border ${
                  isTunecore
                    ? "bg-tunecore-grey-element border-tunecore-light-grey"
                    : "bg-white border-sentric-light-grey"
                }`}
                >
                  <div className="mb-1">{songTemp?.data.title}</div>
                  <div className="flex flex-wrap">
                    <div className={`w-1/2 ${isTunecore ? "text-white" : "text-black"}`}>
                      <p className="text-xs">
                        <span className="font-semibold uppercase">
                          {t("songs:existing-song-performer")}
                        </span>
                        {": "}
                        {songTemp?.data?.performingartists
                          .map(p =>
                            p.performingartistname.replace(/&#44;/g, ","),
                          )
                          .join(", ")}
                      </p>
                      <p className="mt-1 text-xs">
                        <span className="font-semibold uppercase">ISWC:</span>
                        {" "}
                        {songTemp?.data?.iswc || "-"}
                      </p>
                      <p className="mt-1 text-xs">
                        <span className="font-semibold uppercase">
                          {t("songs:duration")}
                        </span>
                        {": "}
                        {songTemp?.data?.durationmins}
                        {" "}
                        mins
                        {" "}
                        {songTemp?.data?.durationsecs}
                        {" "}
                        secs
                      </p>
                    </div>
                    <div className="flex flex-row items-end justify-end w-1/2 mt-1">
                      <RebrandButtonLink
                        id="btnTopWriterAddWriter"
                        onClick={() => {
                          setSelectedSong(null);
                          if (suggestedSong) {
                            setShowManualSearch(true);
                          }
                        }}
                      >
                        {t("songs:existing-song-remove-button")}
                      </RebrandButtonLink>
                    </div>
                  </div>
                </div>
              </>
            )}
            <Spacer />
            <FormError
              mutationError={mutationErrorMessage}
              validationError={validationErrorMessage}
            />

            <div className="flex flex-wrap items-center justify-end">
              <div className="mx-2">
                <RebrandButtonLink
                  onClick={() => {
                    cancel();
                  }}
                >
                  {t("songs:cancel-and-discard")}
                </RebrandButtonLink>
              </div>
              <div className="mx-2">
                <RebrandPrimaryButton
                  onClick={() => addRecording({ suggestion: original })}
                  disabled={!selectedSong || pending || !songTemp?.data}
                  pending={pending}
                >
                  {t("songs:add-recording")}
                </RebrandPrimaryButton>
              </div>
            </div>
          </div>
        )}

        {newSong === "true" && (
          <div
            className={`${
              isTunecore ? "bg-tunecore-black" : "bg-sentric-rebrand-light-grey"
            } rounded-t-none rounded-lg mb-2`}
          >
            <SongsCreateUpdate
              suggestion={original}
              closeForm={() => {
                setNewSong(null);
                setSelectedSong(null);
                // Scroll back to radio groups after a brief delay to allow state update
                setTimeout(() => {
                  radioGroupsRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }, 100);
              }}
              onComplete={() => {
                updateSuggestionStatus({ status: 1, id: original.id });
              }}
            />
          </div>
        )}
      </td>
    </tr>
  );
}

export default SongSuggestionDetails;
